// Customers' shown names (Admin → Customers): the name every page shows for an A2000 customer or a
// Deposco trading partner. One per source + code (`customer-name`); several customers, of either
// source, may share one — MACYS, BLOOMINGDA and Deposco's MACY'S all "MACY'S". None saved, or ""
// (Reset to default), is the source's own rule: A2000 its CUSTOMER code, Deposco its partner's name.
// The hover always says the original: "ROSS STORES INC. · ROSSSTORES · A2000". Display only: A2000
// and Deposco data and saved line records keep the original name; search and the filters match
// both; My customers and assignments go by key (source + code), never by the shown name.

import type { CustomerRef, LineView } from "./assemble";
import { customerKey } from "./assignments";
import type { LineSource } from "./deposco";
import type { RuleBase } from "./routing-rules";
import type { CustomerNameRecord, RoutingRuleRecord } from "./types";

/** A shown name's longest. */
export const SHOWN_NAME_MAX = 40;

/** How a customer shows: the cell's text, its hover, the hover of one alone and the filter's option. */
export interface CustomerFace {
  text: string;
  tip: string;
  full: string;
  label: string;
}

/** Case, spaces and punctuation aside: how a typed customer, or a look-alike name, is compared. */
export const nameKey = (s: string) =>
  s
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/[^A-Z0-9]+/g, "");

/** The source's own rule: A2000 its CUSTOMER code, Deposco its trading partner's name. */
export const defaultShown = (c: { code: string; name: string; source?: LineSource }) =>
  c.source === "Deposco" ? c.name || c.code : c.code || c.name;

/** The hover: the original, "ROSS STORES INC. · ROSSSTORES · A2000"; a workbook row's name alone. */
export const customerTip = (c: { code: string; name: string; source?: LineSource }) =>
  c.code ? [c.name, c.code, c.source ?? "A2000"].filter(Boolean).join(" · ") : c.name;

/**
 * How a customer shows: the shown name saved for its source + code, else the source's rule. A
 * customer known by its typed text alone (a workbook row, a pre-route kept as added: no code) finds a
 * record whose A2000 code or original name it is, case, spaces and punctuation aside; else shows as typed.
 */
export function customerNamer(records: readonly CustomerNameRecord[] = []) {
  const byKey = new Map<string, string>();
  const byText = new Map<string, string>();
  for (const r of records) {
    const shown = String(r.shownName ?? "").trim();
    if (!shown) continue;
    const source: LineSource = r.source === "Deposco" ? "Deposco" : "A2000";
    byKey.set(customerKey(source, String(r.code ?? "")), shown);
    if (source === "A2000" && r.code && !byText.has(nameKey(r.code))) byText.set(nameKey(r.code), shown);
    if (r.customerName && !byText.has(nameKey(r.customerName))) byText.set(nameKey(r.customerName), shown);
  }
  return (c: { code: string; name: string; source?: LineSource }): CustomerFace => {
    const code = c.code.trim();
    const shown = code
      ? (byKey.get(customerKey(c.source ?? "A2000", code)) ?? "")
      : (byText.get(nameKey(c.name)) ?? "");
    const text = shown || defaultShown({ ...c, code });
    const tip = customerTip({ ...c, code });
    return { text, tip, full: tip, label: text };
  };
}
export type CustomerNamer = ReturnType<typeof customerNamer>;

/** The default rule alone: where no record is at hand. */
const DEFAULT_NAMER = customerNamer();

/** A line's customer as the pages show it: as `assemble` named it (`withCustomerNames`), else the default. */
export const customerFace = (c: CustomerRef): CustomerFace => c.face ?? DEFAULT_NAMER(c);

/** Every customer of the lines named once they are built: `assemble`, History's workbook rows, a subset. */
export function withCustomerNames(lines: readonly Pick<LineView, "customers">[], name: CustomerNamer) {
  for (const l of lines) for (const c of l.customers) c.face = name(c);
}

/** A line's customers as shown, each once: "MACY'S", "ROSSSTORES"; a line with none, its names. */
export const shownCustomers = (l: Pick<LineView, "customers" | "customerNames">): string[] =>
  l.customers.length ? [...new Set(l.customers.map((c) => customerFace(c).text))] : [...l.customerNames];

/** A line's customers as one text: "MACY'S, ROSSSTORES". */
export const shownCustomerText = (l: Pick<LineView, "customers" | "customerNames">) => shownCustomers(l).join(", ");

/** What a search or the Customer filter matches a line by: each customer's shown name, original name and code. */
export const customerSearchText = (l: Pick<LineView, "customers" | "customerNames">) =>
  [...shownCustomers(l), ...l.customerNames, ...l.customers.map((c) => c.code)].join(" ");

/**
 * The Customer filter's options: one per shown name — customers sharing one are one option, which
 * keeps every line of any of them; its hover lists their codes. `count`: the lines under it.
 */
export function customerFilterOptions(
  lines: readonly Pick<LineView, "customers" | "customerNames" | "source">[],
): { value: string; label: string; count: number; codes: string[]; tip: string }[] {
  const by = new Map<string, { lines: number; codes: Set<string> }>();
  for (const l of lines) {
    const seen = new Set<string>();
    const faces = l.customers.length
      ? l.customers.map((c) => ({ text: customerFace(c).text, tip: customerFace(c).tip }))
      : l.customerNames.map((n) => ({ text: n, tip: n }));
    for (const f of faces) {
      if (!f.text) continue;
      const had = by.get(f.text) ?? { lines: 0, codes: new Set<string>() };
      if (!seen.has(f.text)) had.lines++;
      seen.add(f.text);
      had.codes.add(f.tip);
      by.set(f.text, had);
    }
  }
  return [...by]
    .map(([value, b]) => {
      const codes = [...b.codes].sort();
      return { value, label: value, count: b.lines, codes, tip: codes.join("\n") };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Whether a line has a customer the Customer filter keeps: by shown name, or by original name (Ask AI, a saved view). */
export const keepsCustomer = (l: Pick<LineView, "customers" | "customerNames">, kept: readonly string[]) =>
  !kept.length || shownCustomers(l).some((s) => kept.includes(s)) || l.customerNames.some((n) => kept.includes(n));

/** One customer of a line under the name it shows (Ask AI's `customers_by_name`). */
export interface CustomerUnder {
  shown: string;
  code: string;
  name: string;
  /** Kept separate (Routing rules): still under its shown name, counted with its own routing window. */
  separate: boolean;
}

const UNDER = " ← ";
const SEPARATE = " · separate";

/**
 * Each customer of a line under the name it shows, as one text: "BEALLS ← BEALLSOUTL · BEALLS OUTLET
 * STORES; BEALLS ← 389596 · BEALL'S FLORIDA · separate". A question by a shown name covers every code
 * under it (`customersUnderOf` reads it back).
 */
export const customersUnder = (l: Pick<LineView, "customers" | "customerNames" | "pts">) =>
  l.customers
    .map((c) => {
      const separate = l.pts.some((p) => p.customerCode === c.code && p.rule?.from === "separate");
      return `${customerFace(c).text}${UNDER}${c.code} · ${c.name}${separate ? SEPARATE : ""}`;
    })
    .join("; ");

export const customersUnderOf = (text: string): CustomerUnder[] =>
  text
    .split("; ")
    .filter((e) => e.includes(UNDER))
    .map((e) => {
      const [shown, rest] = [e.slice(0, e.indexOf(UNDER)), e.slice(e.indexOf(UNDER) + UNDER.length)];
      const separate = rest.endsWith(SEPARATE);
      const body = separate ? rest.slice(0, -SEPARATE.length) : rest;
      const dot = body.indexOf(" · ");
      return {
        shown,
        code: dot < 0 ? body : body.slice(0, dot),
        name: dot < 0 ? "" : body.slice(dot + 3),
        separate,
      };
    });

/** How Ask AI says a line's customers: the shown name, the original with its code in brackets. */
export const customerWords = (l: Pick<LineView, "customers" | "customerNames">) =>
  l.customers.length
    ? [
        ...new Set(
          l.customers.map((c) => {
            const f = customerFace(c);
            const orig = [c.name, c.code].filter((x) => x && x !== f.text).join(" ");
            return orig ? `${f.text} (${orig})` : f.text;
          }),
        ),
      ].join(", ")
    : l.customerNames.join(", ");

// ---------------------------------------------------------------------------------------------
// Admin → Customers

/** One customer as the Admin tab lists it: `routing-customers` and the open lines' customers. */
export interface CustomerNameRow {
  key: string;
  source: LineSource;
  code: string;
  /** The original name: the open lines', else the customer list's, else the one saved. */
  name: string;
  active: boolean;
  openLines: number;
  /** What the pages show now. */
  shown: string;
  /** The source's rule, shown greyed while nothing is saved. */
  defaultShown: string;
  /** Its record, when one was ever saved ("" shown name: back to the default). */
  record: CustomerNameRecord | null;
  isDefault: boolean;
}

/** Name (case and punctuation aside), then A2000 before Deposco, then code. */
export const compareCustomerRows = (
  a: Pick<CustomerNameRow, "name" | "source" | "code">,
  b: Pick<CustomerNameRow, "name" | "source" | "code">,
) =>
  nameKey(a.name).localeCompare(nameKey(b.name)) ||
  (a.source === b.source ? 0 : a.source === "A2000" ? -1 : 1) ||
  a.code.localeCompare(b.code);

/** The Admin list: every customer of `options` (`customerOptions`), and every one with a record. */
export function customerNameRows(
  options: readonly {
    key: string;
    source: LineSource;
    code: string;
    name: string;
    openLines: number;
    active: boolean;
  }[],
  records: readonly CustomerNameRecord[],
): CustomerNameRow[] {
  const name = customerNamer(records);
  const recordOf = new Map(
    records.map((r) => [customerKey(r.source === "Deposco" ? "Deposco" : "A2000", String(r.code ?? "")), r]),
  );
  const rows = new Map(options.map((o) => [o.key, o]));
  for (const [key, r] of recordOf)
    if (!rows.has(key))
      rows.set(key, {
        key,
        source: r.source === "Deposco" ? "Deposco" : "A2000",
        code: String(r.code ?? "").trim(),
        name: String(r.customerName ?? ""),
        openLines: 0,
        active: false,
      });
  return [...rows.values()]
    .map((o) => {
      const record = recordOf.get(o.key) ?? null;
      const original = o.name || String(record?.customerName ?? "");
      return {
        key: o.key,
        source: o.source,
        code: o.code,
        name: original,
        active: o.active,
        openLines: o.openLines,
        shown: name({ code: o.code, name: original, source: o.source }).text,
        defaultShown: defaultShown({ code: o.code, name: original, source: o.source }),
        record,
        isDefault: !String(record?.shownName ?? "").trim(),
      };
    })
    .sort(compareCustomerRows);
}

/** The Admin tab's filters. */
export interface CustomerNameFilters {
  search: string;
  source: "all" | LineSource;
  activeOnly: boolean;
  withOpenLines: boolean;
  named: "all" | "named" | "default";
}

export const DEFAULT_CUSTOMER_NAME_FILTERS: CustomerNameFilters = {
  search: "",
  source: "all",
  activeOnly: true,
  withOpenLines: false,
  named: "all",
};

export const filterCustomerRows = (rows: readonly CustomerNameRow[], f: CustomerNameFilters) => {
  const words = f.search.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (f.source === "all" || r.source === f.source) &&
      (!f.activeOnly || r.active || r.openLines > 0) &&
      (!f.withOpenLines || r.openLines > 0) &&
      (f.named === "all" || (f.named === "named") === !r.isDefault) &&
      (!words || `${r.name} ${r.code} ${r.shown}`.toLowerCase().includes(words)),
  );
};

/** "Group by shown name": each shown name once, its customers under it. */
export const groupByShown = (rows: readonly CustomerNameRow[]) => {
  const by = new Map<string, CustomerNameRow[]>();
  for (const r of rows) by.set(r.shown, [...(by.get(r.shown) ?? []), r]);
  return [...by]
    .map(([shown, of]) => ({ shown, rows: of }))
    .sort((a, b) => nameKey(a.shown).localeCompare(nameKey(b.shown)));
};

/** The shown names already saved, each once: what the bar's input suggests. */
export const usedShownNames = (records: readonly CustomerNameRecord[]) =>
  [...new Set(records.map((r) => String(r.shownName ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));

/** What saving a shown name would do: refused (empty, too long), or an orange "Are you sure?" for a look-alike. */
export type ShownNameCheck =
  | { ok: false; message: string }
  | { ok: true; value: string; warning: { title: string; text: string; lines: string[] } | null };

/**
 * A typed shown name: trimmed, 1–40 characters; the same name may be on any number of customers. One
 * that only looks like a name already used (case, spaces, punctuation, an apostrophe) is a warning.
 */
export function checkShownName(typed: string, used: readonly string[]): ShownNameCheck {
  const value = typed.trim().replace(/\s+/g, " ");
  if (!value) return { ok: false, message: "A shown name can't be empty. Reset to default shows the source's own." };
  if (value.length > SHOWN_NAME_MAX)
    return { ok: false, message: `At most ${SHOWN_NAME_MAX} characters — “${value}” has ${value.length}.` };
  const alike = used.filter((u) => u !== value && nameKey(u) === nameKey(value));
  return {
    ok: true,
    value,
    warning: alike.length
      ? {
          title: "A look-alike is already used — are you sure?",
          text: `“${value}” looks like a shown name already used: the same company would show two ways.`,
          lines: alike,
        }
      : null,
  };
}

/** The change entry's line text: "Customer A2000 ROSSSTORES · ROSS STORES INC.". */
export const customerNameText = (r: Pick<CustomerNameRow, "source" | "code" | "name">) =>
  `Customer ${r.source} ${r.code}${r.name ? ` · ${r.name}` : ""}`;

/** Customer assignments' customers with their saved shown names (`CustomerOption.shown`). */
export const withShownNames = <T extends { key: string }>(
  options: readonly T[],
  records: readonly CustomerNameRecord[],
) => {
  const shown = new Map(
    records
      .filter((r) => String(r.shownName ?? "").trim())
      .map((r) => [
        customerKey(r.source === "Deposco" ? "Deposco" : "A2000", String(r.code ?? "")),
        r.shownName.trim(),
      ]),
  );
  return options.map((o) => (shown.has(o.key) ? { ...o, shown: shown.get(o.key)! } : o));
};

/** `GET /api/routing/admin/customers`: the Admin tab's list (the Admin feature only). */
export interface CustomerNamesPayload {
  customers: CustomerNameRow[];
  /** What a save's routing check reads (`renameImpact`, `routing-rules.ts`). */
  routingRules: RoutingRuleRecord[];
  ruleBase: RuleBase;
}

/** A pick ticket's (SO's) customer, as its line named it; one the line does not hold, by the default rule. */
export const customerFaceOf = (
  line: Pick<LineView, "customers" | "source">,
  c: { code: string; name: string },
): CustomerFace => {
  const ref = line.customers.find((x) => x.code === c.code && (!c.name || x.name === c.name));
  return ref ? customerFace(ref) : DEFAULT_NAMER({ ...c, source: line.source });
};
